import { useLocation, useRoute } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ArrowLeft, Coins, Gem, Info, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { apiRequest } from "@/lib/queryClient";

export default function TransactionDetailPage() {
  const [_, setLocation] = useLocation();
  const [, params] = useRoute("/transaction/:reference");
  
  const { data: transaction, isLoading } = useQuery({
    queryKey: ["transaction", params?.reference],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/currency/transaction/${params?.reference}`);
      if (!res.ok) throw new Error("Failed to fetch transaction details");
      return res.json();
    },
    enabled: !!params?.reference,
  });

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  if (!transaction?.data) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center gap-4">
          <h1 className="text-2xl font-bold">Transaction Not Found</h1>
          <Button onClick={() => setLocation("/shop?tab=topups")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Shop
          </Button>
        </div>
      </div>
    );
  }

  const txn = transaction.data;

  return (
    <div className="container max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="icon" onClick={() => setLocation("/shop?tab=topups")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">Transaction Details</h1>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Payment Information</h2>
              <p className="text-sm text-muted-foreground">
                Transaction Reference: {txn.reference}
              </p>
            </div>
            <div className={txn.status === "completed" 
              ? "px-3 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded-full text-sm font-medium"
              : txn.status === "pending"
                ? "px-3 py-1 bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded-full text-sm font-medium"
                : "px-3 py-1 bg-red-500/20 text-red-400 border border-red-500/30 rounded-full text-sm font-medium"
            }>
              {txn.status}
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Amount Paid:</p>
                <p className="text-2xl font-semibold">₵{(txn.amount / 100).toFixed(2)}</p>
              </div>
              
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Coins Received:</p>
                <p className="text-2xl font-semibold flex items-center gap-2">
                  {txn.coins}
                  {txn.coinType === "premium" ? (
                    <Gem className="h-5 w-5 text-purple-400" />
                  ) : (
                    <Coins className="h-5 w-5 text-yellow-400" />
                  )}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Date:</p>
                <p>{format(new Date(txn.createdAt), "MMM d, yyyy • h:mm:ss a")}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Email:</p>
                <p className="truncate">{txn.email}</p>
              </div>
            </div>

            {txn.metadata && (
              <div className="space-y-2 border-t pt-4">
                <p className="text-sm font-medium text-muted-foreground">Additional Information:</p>
                <pre className="text-xs bg-muted/30 p-3 rounded-md overflow-x-auto">
                  {JSON.stringify(txn.metadata, null, 2)}
                </pre>
              </div>
            )}

            <div className="rounded-md bg-blue-500/10 border border-blue-500/20 p-4 flex items-start gap-2">
              <Info className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-400">
                <p className="font-medium mb-1">Need help with this transaction?</p>
                <p>If you have any questions or concerns about this transaction, please contact our support team.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}